package com.trevorwiebe.timesheet.invoicing.data

import kotlin.time.Duration.Companion.seconds
import kotlinx.coroutines.withTimeout
import kotlinx.coroutines.TimeoutCancellationException
import dev.gitlive.firebase.storage.storage
import dev.gitlive.firebase.functions.functions
import com.trevorwiebe.timesheet.invoicing.data.dto.SendInvoiceResponse
import com.trevorwiebe.timesheet.invoicing.data.dto.SendInvoiceRequest
import com.trevorwiebe.timesheet.invoicing.data.dto.InvoiceIdRequest
import com.trevorwiebe.timesheet.invoicing.data.dto.GeneratePdfResponse
import kotlinx.datetime.LocalDate
import com.trevorwiebe.timesheet.invoicing.domain.model.Payment
import com.trevorwiebe.timesheet.invoicing.data.dto.toPaymentDto
import com.trevorwiebe.timesheet.invoicing.data.dto.toPayment
import com.trevorwiebe.timesheet.invoicing.data.dto.PaymentDto
import com.trevorwiebe.timesheet.core.data.FirestoreListenerRegistry
import com.trevorwiebe.timesheet.core.data.NumberCounter
import com.trevorwiebe.timesheet.invoicing.data.dto.toInvoiceLineDto
import com.trevorwiebe.timesheet.invoicing.domain.model.InvoiceLine
import com.trevorwiebe.timesheet.core.data.Utils.errorWrapper
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.TSResult
import com.trevorwiebe.timesheet.invoicing.data.dto.InvoiceDto
import com.trevorwiebe.timesheet.invoicing.data.dto.InvoicedLoadDto
import com.trevorwiebe.timesheet.invoicing.data.dto.toInvoice
import com.trevorwiebe.timesheet.invoicing.data.dto.toInvoiceDto
import com.trevorwiebe.timesheet.invoicing.domain.InvoiceRepository
import com.trevorwiebe.timesheet.invoicing.domain.model.Invoice
import com.trevorwiebe.timesheet.invoicing.domain.model.InvoiceStatus
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.DocumentReference
import dev.gitlive.firebase.firestore.DocumentSnapshot
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map

const val FIRST_INVOICE_NUMBER = 1001

class InvoiceRepositoryImpl(
    private val firebaseDatabase: Firebase,
    private val coreRepository: CoreRepository,
) : InvoiceRepository {

    private fun org(orgId: String) = firebaseDatabase.firestore.collection("organizations").document(orgId)

    private fun lockRef(orgId: String, loadId: String): DocumentReference =
        org(orgId).collection("invoicedLoads").document(loadId)

    override suspend fun getInvoices(): Flow<TSResult> {
        val orgIdResult = coreRepository.getOrganizationId()
        if (orgIdResult.error != null) return flow { emit(orgIdResult) }
        val orgId = orgIdResult.data as String

        val flow = org(orgId).collection("invoices").snapshots
            .map { snapshot ->
                snapshot.documents
                    .map { doc -> doc.data<InvoiceDto>().toInvoice(id = doc.id) }
                    .sortedByDescending { it.number }
            }
            .map { TSResult(data = it) }
        return FirestoreListenerRegistry.registerFlow(flow)
    }

    override suspend fun createInvoice(invoice: Invoice): TSResult = errorWrapper {
        val orgId = coreRepository.getOrganizationId().data as String
        val counterRef = org(orgId).collection("counters").document("invoices")
        val invoiceRef = org(orgId).collection("invoices").document
        val lockRefs = invoice.loadIds.map { lockRef(orgId, it) }

        val saved = firebaseDatabase.firestore.runTransaction {
            val (number, counter) = invoiceCounter(get(counterRef)).claim()
            // All reads must happen before any write in a Firestore transaction.
            val alreadyInvoiced = lockRefs.count { get(it).exists }
            check(alreadyInvoiced == 0) {
                "$alreadyInvoiced of these loads are already on another invoice. Refresh and try again."
            }
            val numbered = invoice.copy(id = invoiceRef.id, number = number, status = InvoiceStatus.DRAFT)
            set(counterRef, NumberCounter.serializer(), counter)
            set(invoiceRef, InvoiceDto.serializer(), numbered.toInvoiceDto())
            lockRefs.forEach { set(it, InvoicedLoadDto.serializer(), InvoicedLoadDto(invoiceId = invoiceRef.id)) }
            numbered
        }
        TSResult(data = saved)
    }

    override suspend fun deleteDraftInvoice(invoice: Invoice): TSResult = errorWrapper {
        val orgId = coreRepository.getOrganizationId().data as String
        val invoiceRef = org(orgId).collection("invoices").document(invoice.id)
        val counterRef = org(orgId).collection("counters").document("invoices")
        firebaseDatabase.firestore.runTransaction {
            val current = get(invoiceRef)
            val counter = invoiceCounter(get(counterRef))
            check(current.exists) { "This invoice no longer exists." }
            // Checked against the stored copy, not the caller's, so a just-sent invoice can't be deleted.
            val stored = current.data<InvoiceDto>()
            check(stored.status == InvoiceStatus.DRAFT.name) { "Sent invoices can't be deleted." }
            delete(invoiceRef)
            stored.lines.forEach { delete(lockRef(orgId, it.loadId)) }
            set(counterRef, NumberCounter.serializer(), counter.release(stored.number))
        }
        TSResult(data = invoice.id)
    }

    override suspend fun updateInvoiceLines(invoice: Invoice, lines: List<InvoiceLine>, editedOn: LocalDate?): TSResult = errorWrapper {
        val orgId = coreRepository.getOrganizationId().data as String
        val invoiceRef = org(orgId).collection("invoices").document(invoice.id)
        firebaseDatabase.firestore.runTransaction {
            val current = get(invoiceRef)
            check(current.exists) { "This invoice no longer exists." }
            val stored = current.data<InvoiceDto>()
            val total = lines.sumOf { it.total }
            var updated = stored.copy(lines = lines.map { it.toInvoiceLineDto() }, total = total)
            if (stored.status != InvoiceStatus.DRAFT.name) {
                checkNotNull(editedOn) { "This invoice was already sent. Refresh and try again." }
                check(total >= stored.amountPaid) {
                    "The new total is less than what's already been paid on this invoice. Remove a payment first."
                }
                // A blank path makes the next view or send render the corrected PDF.
                updated = updated.copy(editedAt = editedOn.toString(), pdfPath = "")
            }
            set(invoiceRef, InvoiceDto.serializer(), updated)
        }
        TSResult(data = invoice.id)
    }

    override suspend fun markInvoiceSent(invoice: Invoice, sentOn: LocalDate, sentTo: String): TSResult = errorWrapper {
        val orgId = coreRepository.getOrganizationId().data as String
        val invoiceRef = org(orgId).collection("invoices").document(invoice.id)
        firebaseDatabase.firestore.runTransaction {
            val current = get(invoiceRef)
            check(current.exists) { "This invoice no longer exists." }
            check(current.data<InvoiceDto>().status == InvoiceStatus.DRAFT.name) { "This invoice was already sent." }
            update(
                invoiceRef,
                "status" to InvoiceStatus.SENT.name,
                "sentAt" to sentOn.toString(),
                "sentTo" to sentTo,
            )
        }
        TSResult(data = invoice.id)
    }

    override suspend fun getPayments(): Flow<TSResult> {
        val orgIdResult = coreRepository.getOrganizationId()
        if (orgIdResult.error != null) return flow { emit(orgIdResult) }
        val orgId = orgIdResult.data as String

        val flow = org(orgId).collection("payments").snapshots
            .map { snapshot ->
                snapshot.documents
                    .map { doc -> doc.data<PaymentDto>().toPayment(id = doc.id) }
                    .sortedByDescending { it.date }
            }
            .map { TSResult(data = it) }
        return FirestoreListenerRegistry.registerFlow(flow)
    }

    override suspend fun recordPayment(payment: Payment): TSResult = errorWrapper {
        val orgId = coreRepository.getOrganizationId().data as String
        val invoices = org(orgId).collection("invoices")
        val paymentRef = org(orgId).collection("payments").document
        val allocations = payment.allocations.filter { it.amount > 0 }
        firebaseDatabase.firestore.runTransaction {
            val stored = allocations.map { allocation ->
                val snapshot = get(invoices.document(allocation.invoiceId))
                check(snapshot.exists) { "Invoice #${allocation.invoiceNumber} no longer exists." }
                snapshot.data<InvoiceDto>()
            }
            allocations.zip(stored).forEach { (allocation, invoice) ->
                check(invoice.status == InvoiceStatus.SENT.name) {
                    "Invoice #${invoice.number} hasn't been sent, so it can't take a payment."
                }
                check(allocation.amount <= invoice.total - invoice.amountPaid) {
                    "Invoice #${invoice.number} doesn't owe that much anymore. Refresh and try again."
                }
            }
            set(paymentRef, PaymentDto.serializer(), payment.copy(allocations = allocations).toPaymentDto())
            allocations.zip(stored).forEach { (allocation, invoice) ->
                update(invoices.document(allocation.invoiceId), "amountPaid" to invoice.amountPaid + allocation.amount)
            }
        }
        TSResult(data = paymentRef.id)
    }

    override suspend fun deletePayment(payment: Payment): TSResult = errorWrapper {
        val orgId = coreRepository.getOrganizationId().data as String
        val invoices = org(orgId).collection("invoices")
        val paymentRef = org(orgId).collection("payments").document(payment.id)
        firebaseDatabase.firestore.runTransaction {
            val current = get(paymentRef)
            check(current.exists) { "This payment was already removed." }
            val stored = current.data<PaymentDto>()
            val invoiceSnapshots = stored.allocations.map { get(invoices.document(it.invoiceId)) }
            stored.allocations.zip(invoiceSnapshots).forEach { (allocation, snapshot) ->
                if (snapshot.exists) {
                    val paid = snapshot.data<InvoiceDto>().amountPaid
                    update(snapshot.reference, "amountPaid" to (paid - allocation.amount).coerceAtLeast(0))
                }
            }
            delete(paymentRef)
        }
        TSResult(data = payment.id)
    }

    override suspend fun generateInvoicePdf(invoiceId: String): TSResult = slowCall {
        val result = Firebase.functions.httpsCallable("generateInvoicePdf").invoke(InvoiceIdRequest(invoiceId))
        TSResult(data = result.data<GeneratePdfResponse>().pdfPath)
    }

    override suspend fun getPdfUrl(pdfPath: String): TSResult = errorWrapper {
        TSResult(data = Firebase.storage.reference.child(pdfPath).getDownloadUrl())
    }

    override suspend fun sendInvoice(invoiceId: String, to: List<String>, documentUrls: List<String>): TSResult = slowCall {
        val result = Firebase.functions.httpsCallable("sendInvoice")
            .invoke(SendInvoiceRequest(invoiceId, to, documentUrls))
        TSResult(data = result.data<SendInvoiceResponse>())
    }

    private fun invoiceCounter(snapshot: DocumentSnapshot): NumberCounter =
        if (snapshot.exists) snapshot.data<NumberCounter>()
        else NumberCounter(FIRST_INVOICE_NUMBER)

    /**
     * Like errorWrapper, but allows longer: rendering and attaching load documents can outlast its
     * 15-second limit, and timing out early would report a failure for a send that still completes.
     */
    private suspend fun slowCall(action: suspend () -> TSResult): TSResult = try {
        withTimeout(110.seconds) { action() }
    } catch (e: TimeoutCancellationException) {
        TSResult(error = "This is taking longer than expected. Check the invoice before trying again.")
    } catch (e: Exception) {
        TSResult(error = e.message ?: e.toString())
    }
}
